import _cloneDeep from 'lodash/cloneDeep';
import {
  BaseMetricsRequest,
  CategoryType,
  ContentMetricType,
  GetScoreForContentMetricByCategoryTypeRequest,
  GetScoreForContentMetricRequest,
  TimePeriodRange,
  VoiceAndToneMatchType,
  ContentUtilizationFilters,
  GetKeywordOpportunitiesRequest
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/ContentServerProxy/types';

export enum KeywordProductivityType {
  Productive = 'Productive',
  Unproductive = 'Unproductive'
}

/**
 * Builder for creating content metrics requests.
 * We should extend this as needed.
 */
export class ContentMetricsRequestBuilder {
  protected request: BaseMetricsRequest;

  protected contentMetricType: ContentMetricType;

  protected categoryType: CategoryType;

  protected voiceAndToneMatchTypes: VoiceAndToneMatchType[];

  protected filters: ContentUtilizationFilters;

  protected keywordProductivityType: KeywordProductivityType | undefined;

  constructor(
    baseRequest?: BaseMetricsRequest,
    contentMetricType?: ContentMetricType,
    categoryType?: CategoryType,
    voiceAndToneMatchTypes?: VoiceAndToneMatchType[],
    keywordProductivityType?: KeywordProductivityType
  ) {
    this.request = baseRequest
      ? _cloneDeep(baseRequest)
      : {
          retailerId: null,
          stacklineSkus: [],
          brandIds: [],
          categoryIds: [],
          subcategoryIds: [],
          segmentIds: [],
          businessUnits: [],
          startWeekId: null,
          endWeekId: null,
          resultCount: 12000,
          sortField: '',
          keywordProductivityType: undefined
        };
    this.contentMetricType = contentMetricType;
    this.categoryType = categoryType;
    this.voiceAndToneMatchTypes = voiceAndToneMatchTypes;
    this.keywordProductivityType = keywordProductivityType;
    this.filters = {
      filterTitleInaccuracies: false,
      filterBulletPointsInaccuracies: false,
      filterImageInaccuracies: false,
      filterDescriptionInaccuracies: false,
      filterVideoInaccuracies: false,
      filterItemHighlightsInaccuracies: false
    };
  }

  public setMainEntity(mainEntity: any): this {
    const { type, id } = mainEntity;

    switch (type) {
      case 'category':
        return this.setCategoryIds([+id]);
      case 'subcategory':
        return this.setSubcategoryIds([+id]);
      case 'brand':
        return this.setBrandIds([+id]);
      case 'product':
        return this.setStacklineSkus([id]);
      case 'segment':
        return this.setSegmentIds([id]);
      case 'businessunit':
        return this.setBusinessUnits([id]);

      default:
        console.warn(`Unknown entity type: ${type}`);
        return this;
    }
  }

  public setKeywordProductivityType(keywordProductivityType: KeywordProductivityType): this {
    this.request.keywordProductivityType = keywordProductivityType;
    return this;
  }

  public setMainTimePeriod(mainTimePeriod: TimePeriodRange): this {
    this.setStartWeekId(mainTimePeriod.startWeek).setEndWeekId(mainTimePeriod.endWeek);
    return this;
  }

  public setRetailerId(retailerId: number): this {
    this.request.retailerId = retailerId;
    return this;
  }

  public setStacklineSkus(stacklineSkus: string[]): this {
    this.request.stacklineSkus = stacklineSkus;
    return this;
  }

  public setBrandIds(brandIds: number[]): this {
    this.request.brandIds = brandIds;
    return this;
  }

  public setCategoryIds(categoryIds: number[]): this {
    this.request.categoryIds = categoryIds;
    return this;
  }

  public setSubcategoryIds(subcategoryIds: number[]): this {
    this.request.subcategoryIds = subcategoryIds;
    return this;
  }

  public setSegmentIds(segmentIds: string[]): this {
    this.request.segmentIds = segmentIds;
    return this;
  }

  public setBusinessUnits(businessUnits: string[]): this {
    this.request.businessUnits = businessUnits;
    return this;
  }

  public setSortField(sortField: string): this {
    this.request.sortField = sortField;
    return this;
  }

  public setStartWeekId(startWeekId: number): this {
    this.request.startWeekId = startWeekId;
    return this;
  }

  public setEndWeekId(endWeekId: number): this {
    this.request.endWeekId = endWeekId;
    return this;
  }

  public setContentMetricType(contentMetricType: ContentMetricType): this {
    this.contentMetricType = contentMetricType;
    return this;
  }

  public setCategoryType(categoryType: CategoryType): this {
    this.categoryType = categoryType;
    return this;
  }

  public setVoiceAndToneMatchTypes(voiceAndToneMatchTypes: VoiceAndToneMatchType[]): this {
    this.voiceAndToneMatchTypes = voiceAndToneMatchTypes;
    return this;
  }

  public setFilters(filters: Partial<ContentUtilizationFilters>): this {
    this.filters = {
      ...this.filters,
      ...filters
    };

    return this;
  }

  public setPageNumber(pageNumber: number): ContentMetricsRequestBuilder {
    this.request.pageNumber = pageNumber;
    return this;
  }

  public setPageSize(pageSize: number): ContentMetricsRequestBuilder {
    this.request.pageSize = pageSize;
    return this;
  }

  public clone(): ContentMetricsRequestBuilder {
    return new ContentMetricsRequestBuilder(
      _cloneDeep(this.request),
      this.contentMetricType,
      this.categoryType,
      this.voiceAndToneMatchTypes
    );
  }

  public build(): BaseMetricsRequest | GetScoreForContentMetricRequest | GetScoreForContentMetricByCategoryTypeRequest {
    return {
      ...this.request,
      ...(this.contentMetricType ? { contentMetricType: this.contentMetricType } : {}),
      ...(this.categoryType ? { categoryType: this.categoryType } : {}),
      ...(this.voiceAndToneMatchTypes ? { voiceAndToneMatchTypes: this.voiceAndToneMatchTypes } : {}),
      ...(this.filters ? this.filters : {})
    };
  }
}

export class GetContentMetricsForProductRequestBuilder {
  private retailerSku: string;

  private stacklineSku: string;

  private retailerId: number;

  private weekId: number;

  constructor(retailerId: number, weekId: number, retailerSku?: string, stacklineSku?: string) {
    this.retailerSku = retailerSku;
    this.stacklineSku = stacklineSku;
    this.retailerId = retailerId;
    this.weekId = weekId;
  }

  public setRetailerSku(retailerSku: string): GetContentMetricsForProductRequestBuilder {
    this.retailerSku = retailerSku;
    return this;
  }

  public setRetailerId(retailerId: number): GetContentMetricsForProductRequestBuilder {
    this.retailerId = retailerId;
    return this;
  }

  public setStacklineSku(stacklineSku: string): GetContentMetricsForProductRequestBuilder {
    this.stacklineSku = stacklineSku;
    return this;
  }

  public setWeekId(weekId: number): GetContentMetricsForProductRequestBuilder {
    this.weekId = weekId;
    return this;
  }

  public clone(): GetContentMetricsForProductRequestBuilder {
    return new GetContentMetricsForProductRequestBuilder(
      this.retailerId,
      this.weekId,
      this.retailerSku,
      this.stacklineSku
    );
  }

  public build() {
    return {
      retailerSku: this.retailerSku,
      retailerId: this.retailerId,
      stacklineSku: this.stacklineSku,
      weekId: this.weekId
    };
  }
}

export class GetWeightedKeywordsAndPhrasesForProductRequestBuilder {
  private retailerSku: string;

  private retailerId: number;

  private weekId: number;

  private contentMetricType: ContentMetricType.KeywordOrganicTraffic | ContentMetricType.KeywordSearchVolume;

  constructor(
    retailerId: number,
    weekId: number,
    retailerSku?: string,
    contentMetricType:
      | ContentMetricType.KeywordOrganicTraffic
      | ContentMetricType.KeywordSearchVolume = ContentMetricType.KeywordOrganicTraffic
  ) {
    this.retailerSku = retailerSku;
    this.retailerId = retailerId;
    this.weekId = weekId;
    this.contentMetricType = contentMetricType;
  }

  public setRetailerSku(retailerSku: string): GetWeightedKeywordsAndPhrasesForProductRequestBuilder {
    this.retailerSku = retailerSku;
    return this;
  }

  public setRetailerId(retailerId: number): GetWeightedKeywordsAndPhrasesForProductRequestBuilder {
    this.retailerId = retailerId;
    return this;
  }

  public setWeekId(weekId: number): GetWeightedKeywordsAndPhrasesForProductRequestBuilder {
    this.weekId = weekId;
    return this;
  }

  public setContentMetricType(
    contentMetricType: ContentMetricType.KeywordOrganicTraffic | ContentMetricType.KeywordSearchVolume
  ): GetWeightedKeywordsAndPhrasesForProductRequestBuilder {
    this.contentMetricType = contentMetricType;
    return this;
  }

  public clone(): GetWeightedKeywordsAndPhrasesForProductRequestBuilder {
    return new GetWeightedKeywordsAndPhrasesForProductRequestBuilder(
      this.retailerId,
      this.weekId,
      this.retailerSku,
      this.contentMetricType
    );
  }

  public build() {
    return {
      retailerSku: this.retailerSku,
      retailerId: this.retailerId,
      weekId: this.weekId,
      contentMetricType: this.contentMetricType
    };
  }
}

/**
 * Builder class for constructing keyword opportunities requests
 */
export class KeywordOpportunitiesRequestBuilder {
  private request: GetKeywordOpportunitiesRequest;

  constructor(retailerId: number, retailerSku: string, weekId: string) {
    this.request = {
      retailerId: retailerId,
      retailerSku,
      weekId
    };
  }

  public setRetailerId(retailerId: number): KeywordOpportunitiesRequestBuilder {
    this.request.retailerId = retailerId;
    return this;
  }

  public setRetailerSku(retailerSku: string): KeywordOpportunitiesRequestBuilder {
    this.request.retailerSku = retailerSku;
    return this;
  }

  public setWeekId(weekId: string): KeywordOpportunitiesRequestBuilder {
    this.request.weekId = weekId;
    return this;
  }

  public build(): GetKeywordOpportunitiesRequest {
    return { ...this.request };
  }
}

export class VariationsRequestBuilder extends ContentMetricsRequestBuilder {
  private requestType: string;
  private includeMetrics: boolean;
  private variationsFilterType: string | undefined;

  constructor(
    baseRequest?: BaseMetricsRequest,
    requestType?: string,
    includeMetrics: boolean = true,
    variationsFilterType?: string
  ) {
    super(baseRequest);
    this.requestType = requestType;
    this.includeMetrics = includeMetrics;
    this.variationsFilterType = variationsFilterType;
  }

  public setRequestType(requestType: string): VariationsRequestBuilder {
    this.requestType = requestType;
    return this;
  }

  public setIncludeMetrics(includeMetrics: boolean): VariationsRequestBuilder {
    this.includeMetrics = includeMetrics;
    return this;
  }

  public setOffset(offset: number): VariationsRequestBuilder {
    this.request.offset = offset;
    return this;
  }

  public setVariationsFilterType(variationsFilterType: string | undefined): VariationsRequestBuilder {
    this.variationsFilterType = variationsFilterType;
    return this;
  }

  public clone(): VariationsRequestBuilder {
    const cloned = new VariationsRequestBuilder(
      _cloneDeep(this.request),
      this.requestType,
      this.includeMetrics,
      this.variationsFilterType
    );
    return cloned;
  }

  public build() {
    return {
      ...super.build(),
      requestType: this.requestType,
      includeMetrics: this.includeMetrics,
      variationsFilterType: this.variationsFilterType
    };
  }
}
